import { useCallback } from "react";

import { getSceneState } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { clearSelectedVisualMeshes } from "../../visual-mesh-multi-selection";
import { findGeomNode } from "../../utils/geomSelection";
import { sceneGraphNodeFillStyle } from "../../utils/sceneGraphNodeAppearance";
import { isSceneGraphSiteNode } from "./gestures";

export function useSceneGraphNodeInteractions({
  bodies,
  selectedBody,
  selectedBodies,
  selectedJoint,
  selectedActuator,
  selectedSite,
  selectedGeom,
  selectedInertial,
  selectedMesh,
  selectedCamera,
  lastSelectionSource,
  selectJoint,
  selectSite,
  selectGeom,
  selectInertial,
  selectCamera,
  toggleBodyInSelection,
  multiSelectedMeshes,
  setMultiSelectedMeshes,
  multiSelectedGeoms,
  setMultiSelectedGeoms,
  multiSelectedSites,
  setMultiSelectedSites,
  multiSelectedInertials,
  setMultiSelectedInertials,
  multiSelectedJoints,
  setMultiSelectedJoints,
  clearBatchSelection,
  reparentPick,
}) {
  const getPrimitiveVisualGeomKey = useCallback(
    (node) =>
      node.type === "mesh" && !node.meshId && node.parentBodyUuid && node.uuid
        ? `${node.parentBodyUuid}:${node.uuid}`
        : null,
    []
  );

  const selectBodyForSceneGraphTarget = useCallback((bodyUuid, source) => {
    if (!bodyUuid || !source) return;
    const selectionActions = useSelectionStore.getState();
    const alreadySelectedBody = selectionActions.selectedBody === bodyUuid;
    const alreadySelectedSource = selectionActions.lastSelectionSource === source;

    selectionActions.setLastSelectionSource(source);
    if (alreadySelectedBody) {
      if (!alreadySelectedSource) {
        selectionActions.selectBody(null);
        selectionActions.setLastSelectionSource(source);
        selectionActions.selectBody(bodyUuid);
      }
      return;
    }
    selectionActions.selectBody(bodyUuid);
  }, []);

  const openObjectPropertiesFromSceneGraphRoot = useCallback(
    (bodyUuid) => {
      if (!bodyUuid) return;
      selectBodyForSceneGraphTarget(bodyUuid, "sceneTreeObject");
      window.dispatchEvent(new CustomEvent("open-properties-panel"));
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("scene-graph-object-movement-attention", {
            detail: { bodyUuid },
          })
        );
      }, 80);
    },
    [selectBodyForSceneGraphTarget]
  );

  const isInspectableJointNode = useCallback((node) => {
    if (node?.type !== "joint" || !node.uuid) return false;
    const joint = getSceneState().joints?.[node.uuid];
    return joint?.kind !== "free";
  }, []);

  const isNodeSelected = useCallback(
    (node) => {
      switch (node.type) {
        case "body":
          return (
            selectedBody === node.uuid &&
            lastSelectionSource !== "sceneTreeObject" &&
            lastSelectionSource !== "objectLibrary"
          );
        case "joint":
          return isInspectableJointNode(node) && selectedJoint === node.uuid;
        case "actuator":
          return selectedActuator === node.uuid;
        case "site":
        case "attachment_site":
          return selectedSite === node.uuid;
        case "geom":
          return node.parentBodyUuid && node.uuid
            ? selectedGeom === `${node.parentBodyUuid}:${node.uuid}`
            : false;
        case "inertial":
          return node.parentBodyUuid && node.uuid
            ? selectedInertial === `${node.parentBodyUuid}:${node.uuid}`
            : false;
        case "camera":
          return selectedCamera === node.uuid;
        case "mesh":
          if (node.parentBodyUuid && node.uuid) {
            return selectedGeom === `${node.parentBodyUuid}:${node.uuid}`;
          }
          if (!node.meshId) return false;
          return selectedMesh === node.meshId;
        default:
          return false;
      }
    },
    [
      selectedBody,
      lastSelectionSource,
      isInspectableJointNode,
      selectedJoint,
      selectedActuator,
      selectedSite,
      selectedGeom,
      selectedInertial,
      selectedMesh,
      selectedCamera,
    ]
  );

  const getSelectedVisualMeshIdForSceneGraph = useCallback(() => {
    if (selectedMesh) return String(selectedMesh);
    if (!selectedGeom) return null;
    const resolved = findGeomNode(bodies, selectedGeom);
    const meshId = resolved?.geom?.visualOnly === true ? resolved.geom.mesh : null;
    return meshId ? String(meshId) : null;
  }, [bodies, selectedGeom, selectedMesh]);

  const handleNodeClick = useCallback(
    (node, event) => {
      if (node.type === "body" && (event?.metaKey || event?.ctrlKey)) {
        setMultiSelectedJoints(new Set());
        setMultiSelectedInertials(new Set());
        setMultiSelectedSites(new Set());
        setMultiSelectedGeoms(new Set());
        setMultiSelectedMeshes(new Set());
        if (node.uuid) toggleBodyInSelection(node.uuid);
        return;
      }
      if (node.type === "mesh" && (event?.metaKey || event?.ctrlKey)) {
        if (!node.meshId) {
          const geomKey = getPrimitiveVisualGeomKey(node);
          if (!geomKey) return;
          useSelectionStore.getState().setSelectedBodies([]);
          clearSelectedVisualMeshes();
          setMultiSelectedSites(new Set());
          setMultiSelectedInertials(new Set());
          setMultiSelectedJoints(new Set());
          setMultiSelectedGeoms((prev) => {
            const next = new Set(prev);
            if (next.has(geomKey)) {
              next.delete(geomKey);
            } else {
              next.add(geomKey);
            }
            return next;
          });
          return;
        }
        setMultiSelectedJoints(new Set());
        setMultiSelectedInertials(new Set());
        setMultiSelectedSites(new Set());
        setMultiSelectedGeoms(new Set());
        const selectionActions = useSelectionStore.getState();
        selectionActions.setSelectedBodies([]);
        setMultiSelectedMeshes((prev) => {
          const next = new Set(prev);
          if (next.size === 0) {
            const selectedVisualMeshId = getSelectedVisualMeshIdForSceneGraph();
            if (selectedVisualMeshId) next.add(selectedVisualMeshId);
          }
          if (next.has(node.meshId)) {
            next.delete(node.meshId);
          } else {
            next.add(node.meshId);
          }
          if (next.size === 1) {
            selectionActions.selectMesh([...next][0]);
          } else {
            selectionActions.clearAllSelections();
          }
          return next;
        });
        return;
      }
      if (node.type === "geom" && (event?.metaKey || event?.ctrlKey)) {
        if (!node.parentBodyUuid || !node.uuid) return;
        const geomKey = `${node.parentBodyUuid}:${node.uuid}`;
        useSelectionStore.getState().setSelectedBodies([]);
        setMultiSelectedMeshes(new Set());
        setMultiSelectedSites(new Set());
        setMultiSelectedInertials(new Set());
        setMultiSelectedJoints(new Set());
        setMultiSelectedGeoms((prev) => {
          const next = new Set(prev);
          if (next.has(geomKey)) {
            next.delete(geomKey);
          } else {
            next.add(geomKey);
          }
          return next;
        });
        return;
      }
      if (isSceneGraphSiteNode(node) && (event?.metaKey || event?.ctrlKey)) {
        if (!node.uuid) return;
        useSelectionStore.getState().setSelectedBodies([]);
        setMultiSelectedMeshes(new Set());
        setMultiSelectedGeoms(new Set());
        setMultiSelectedInertials(new Set());
        setMultiSelectedJoints(new Set());
        setMultiSelectedSites((prev) => {
          const next = new Set(prev);
          if (next.has(node.uuid)) {
            next.delete(node.uuid);
          } else {
            next.add(node.uuid);
          }
          return next;
        });
        return;
      }
      if (node.type === "inertial" && (event?.metaKey || event?.ctrlKey)) {
        if (!node.parentBodyUuid || !node.uuid) return;
        const inertialKey = `${node.parentBodyUuid}:${node.uuid}`;
        useSelectionStore.getState().setSelectedBodies([]);
        setMultiSelectedMeshes(new Set());
        setMultiSelectedGeoms(new Set());
        setMultiSelectedSites(new Set());
        setMultiSelectedJoints(new Set());
        setMultiSelectedInertials((prev) => {
          const next = new Set(prev);
          if (next.has(inertialKey)) {
            next.delete(inertialKey);
          } else {
            next.add(inertialKey);
          }
          return next;
        });
        return;
      }
      if (node.type === "joint" && (event?.metaKey || event?.ctrlKey)) {
        if (!isInspectableJointNode(node)) return;
        useSelectionStore.getState().setSelectedBodies([]);
        setMultiSelectedMeshes(new Set());
        setMultiSelectedGeoms(new Set());
        setMultiSelectedSites(new Set());
        setMultiSelectedInertials(new Set());
        setMultiSelectedJoints((prev) => {
          const next = new Set(prev);
          if (next.has(node.uuid)) {
            next.delete(node.uuid);
          } else {
            next.add(node.uuid);
          }
          return next;
        });
        return;
      }
      if (
        selectedBodies.length > 1 ||
        multiSelectedMeshes.size > 0 ||
        multiSelectedGeoms.size > 0 ||
        multiSelectedSites.size > 0 ||
        multiSelectedInertials.size > 0 ||
        multiSelectedJoints.size > 0
      ) {
        clearBatchSelection();
      }
      const selectionActions = useSelectionStore.getState();
      switch (node.type) {
        case "body":
          selectBodyForSceneGraphTarget(node.uuid, "sceneTree");
          break;
        case "joint":
          if (!isInspectableJointNode(node)) return;
          selectionActions.setLastSelectionSource("sceneTree");
          selectJoint(node.uuid);
          break;
        case "site":
        case "attachment_site":
          selectionActions.setLastSelectionSource("sceneTree");
          selectSite(node.uuid);
          break;
        case "geom":
          selectionActions.setLastSelectionSource("sceneTree");
          if (node.parentBodyUuid && node.uuid) selectGeom(`${node.parentBodyUuid}:${node.uuid}`);
          break;
        case "inertial":
          selectionActions.setLastSelectionSource("sceneTree");
          if (node.parentBodyUuid && node.uuid)
            selectInertial(`${node.parentBodyUuid}:${node.uuid}`);
          break;
        case "mesh":
          selectionActions.setLastSelectionSource("sceneTree");
          if (node.parentBodyUuid && node.uuid) {
            selectGeom(`${node.parentBodyUuid}:${node.uuid}`);
          }
          break;
        case "actuator": {
          const actuator = getSceneState().actuators?.[node.uuid];
          selectionActions.setLastSelectionSource("sceneTree");
          selectionActions.selectActuatorInParent(
            node.uuid,
            actuator?.joint ?? null,
            actuator?.tendonUuid ?? null
          );
          window.dispatchEvent(new CustomEvent("open-properties-panel"));
          break;
        }
        case "camera":
          selectionActions.setLastSelectionSource("sceneTree");
          selectCamera(node.uuid);
          window.dispatchEvent(new CustomEvent("open-properties-panel"));
          break;
        case "tendon":
        case "equality":
          {
            const nodeUuid =
              node.type === "tendon"
                ? node.tendonUuid || node.uuid || null
                : node.equalityUuid || node.uuid || null;
            window.dispatchEvent(
              new CustomEvent("open-scene-element-editor", {
                detail: { type: node.type, uuid: nodeUuid },
              })
            );
          }
          break;
        default:
          break;
      }
    },
    [
      selectJoint,
      isInspectableJointNode,
      selectSite,
      selectGeom,
      selectInertial,
      selectCamera,
      selectBodyForSceneGraphTarget,
      toggleBodyInSelection,
      selectedBodies.length,
      multiSelectedMeshes.size,
      multiSelectedGeoms.size,
      multiSelectedSites.size,
      multiSelectedInertials.size,
      multiSelectedJoints.size,
      clearBatchSelection,
      getPrimitiveVisualGeomKey,
      getSelectedVisualMeshIdForSceneGraph,
      setMultiSelectedMeshes,
      setMultiSelectedGeoms,
      setMultiSelectedSites,
      setMultiSelectedInertials,
      setMultiSelectedJoints,
    ]
  );

  const onSceneGraphEditorCurvePointerDown = useCallback(
    (event) => {
      if (reparentPick) return;
      event.stopPropagation();
    },
    [reparentPick]
  );

  const onSceneGraphEditorCurveClick = useCallback(
    (event, editorType, elementUuid) => {
      if (reparentPick || !elementUuid) return;
      event.stopPropagation();
      if (
        selectedBodies.length > 1 ||
        multiSelectedMeshes.size > 0 ||
        multiSelectedGeoms.size > 0 ||
        multiSelectedSites.size > 0 ||
        multiSelectedInertials.size > 0 ||
        multiSelectedJoints.size > 0
      ) {
        clearBatchSelection();
      }
      window.dispatchEvent(
        new CustomEvent("open-scene-element-editor", {
          detail: { type: editorType, uuid: elementUuid },
        })
      );
    },
    [
      reparentPick,
      selectedBodies.length,
      multiSelectedMeshes.size,
      multiSelectedGeoms.size,
      multiSelectedSites.size,
      multiSelectedInertials.size,
      multiSelectedJoints.size,
      clearBatchSelection,
    ]
  );

  const getNodeFill = useCallback(
    (node) => {
      const sel = isNodeSelected(node);
      const batchSel =
        (node.type === "body" && selectedBodies.includes(node.uuid)) ||
        (node.type === "mesh" &&
          (node.meshId
            ? multiSelectedMeshes.has(node.meshId)
            : (() => {
                const geomKey = getPrimitiveVisualGeomKey(node);
                return !!geomKey && multiSelectedGeoms.has(geomKey);
              })())) ||
        (node.type === "geom" &&
          node.parentBodyUuid &&
          node.uuid &&
          multiSelectedGeoms.has(`${node.parentBodyUuid}:${node.uuid}`)) ||
        (isSceneGraphSiteNode(node) && multiSelectedSites.has(node.uuid)) ||
        (node.type === "inertial" &&
          node.parentBodyUuid &&
          node.uuid &&
          multiSelectedInertials.has(`${node.parentBodyUuid}:${node.uuid}`)) ||
        (node.type === "joint" && multiSelectedJoints.has(node.uuid));
      return sceneGraphNodeFillStyle(node.type, sel, batchSel);
    },
    [
      isNodeSelected,
      selectedBodies,
      multiSelectedMeshes,
      multiSelectedGeoms,
      multiSelectedSites,
      multiSelectedInertials,
      multiSelectedJoints,
      getPrimitiveVisualGeomKey,
    ]
  );

  return {
    getPrimitiveVisualGeomKey,
    selectBodyForSceneGraphTarget,
    openObjectPropertiesFromSceneGraphRoot,
    handleNodeClick,
    onSceneGraphEditorCurvePointerDown,
    onSceneGraphEditorCurveClick,
    getNodeFill,
  };
}
